import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import SectionEditForm from './SectionEditForm';
import AddressForm from '../../AddressForm';
import MapClickHandler from '../../MapClickHandler';
import client from '../../../lib/feathers';
import { setListing } from '../../../lib/listingsSlice';

/**
 * Where you are, and where the pin sits — two checklist items in one place,
 * because they are the same question asked twice.
 *
 * The pin matters more than it looks: geocoding puts a lot of wineries on the
 * road outside, or at the winemaker's house, and the owner is the only person
 * who knows which gate visitors should actually drive through. Sim's call that
 * an owner can set their own pin is what makes this worth surfacing.
 *
 * Deliberately does NOT include the host-listing toggle, which the big form
 * mixes in here. "Is this venue inside another venue" is a different question
 * from "is this address right", and it should not be in the way of confirming
 * an address.
 */
const DEFAULT_CENTRE = [45.52, -122.68]; // Portland, so the map is never blank

export default function AddressEditForm() {
  const { lid } = useParams();
  const dispatch = useDispatch();
  const listing = useSelector((s) => s.listings.selected);
  const currentUser = useSelector((s) => s.auth.user);
  const role = currentUser?.role === 'admin' ? 'admin' : 'industry';

  const [address, setAddress] = useState({});
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let row = listing?.id === lid ? listing : null;
      if (!row) {
        try {
          row = await client.service(`${role}/listing`).get(lid);
          if (cancelled) return;
          dispatch(setListing(row));
        } catch (e) {
          console.log('could not load listing', e);
          return;
        }
      }
      setAddress({ ...(row.address || {}) });
      const coords = row.location?.coordinates;
      if (Array.isArray(coords) && coords.length >= 2) {
        setMarker({ lat: coords[1], lon: coords[0] });
      }
    })();
    return () => { cancelled = true; };
  }, [lid, listing?.id, dispatch]);

  if (!listing || listing.id !== lid) return null;

  const onLocationChange = ({ latitude, longitude }) => {
    setMarker({ lat: latitude, lon: longitude });
  };

  const centre = marker ? [marker.lat, marker.lon] : DEFAULT_CENTRE;

  const buildPatch = () => {
    const patch = { address };
    if (marker) {
      patch.location = { type: 'Point', coordinates: [marker.lon, marker.lat] };
    }
    return patch;
  };

  return (
    <SectionEditForm
      listing={listing}
      title="Address and map pin"
      description="Where visitors should drive to. Drag or tap the map to move the pin — automatic geocoding often lands on the road outside rather than the gate you want people to use."
      backTo={`/${role}/listings/${lid}`}
      buildPatch={buildPatch}
      onSaved={(updated) => dispatch(setListing({
        ...listing,
        address: updated.address ?? address,
        location: updated.location ?? listing.location,
      }))}
    >
      <div className="grid grid-cols-6 gap-6">
        <AddressForm address={address} onChange={setAddress} disabled={false} />
      </div>

      <div>
        <span className="block text-sm font-medium text-gray-700">Map pin</span>
        <p className="mt-1 text-sm text-gray-500">
          {marker
            ? 'Tap the map to move it.'
            : 'No pin yet — tap the map to place one.'}
        </p>
        <div className="mt-2 h-64 rounded-md overflow-hidden border border-gray-200">
          <MapContainer
            center={centre}
            zoom={marker ? 15 : 9}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {marker && <Marker position={[marker.lat, marker.lon]} />}
            <MapClickHandler onLocationChange={onLocationChange} />
          </MapContainer>
        </div>
        {marker && (
          <p className="mt-2 text-xs text-gray-400 font-mono">
            {marker.lat.toFixed(5)}, {marker.lon.toFixed(5)}
          </p>
        )}
      </div>
    </SectionEditForm>
  );
}
